import { t } from "@lingui/macro";
import {
  type GetSubmittedRfiCountQuery,
  type RequestForInvoicing,
  RequestForInvoicingTypeEnum,
  useGetRequestForInvoicingTypeQuery,
  useGetSubmittedRfiCountQuery,
} from "@src/__generated__/graphql";
import { useNavigationPages } from "@src/components/widgets/AppSubNav/hooks";
import type { NavigationHook } from "@src/components/widgets/AppSubNav/hooks/types";

function getEditTitleFromType(
  type: RequestForInvoicing["type"] | null | undefined,
): string | undefined {
  if (!type) return;

  switch (type) {
    case RequestForInvoicingTypeEnum.Invoice:
      return t`Edit request for invoice`;
    case RequestForInvoicingTypeEnum.CreditNote:
      return t`Edit credit note`;
    case RequestForInvoicingTypeEnum.Proforma:
      return t`Edit request for proforma invoice`;
  }
}

export function useRequestsForInvoicingNavigation(
  invoiceId?: string,
): NavigationHook<GetSubmittedRfiCountQuery["getSubmittedRfiCount"]> {
  const { data, loading } = useGetSubmittedRfiCountQuery({
    fetchPolicy: "cache-and-network",
  });
  const rfiType = useGetRequestForInvoicingTypeQuery({
    variables: {
      id: invoiceId ?? "",
    },
    skip: !invoiceId,
    fetchPolicy: "cache-and-network",
  });

  const { activePage, activeSubpage, allPages, visiblePages } =
    useNavigationPages([
      {
        title: () => t`Request for invoicing`,
        href: {
          pathname: "/invoices/for-invoicing",
        },
        subPages: [
          {
            title: () => t`New request for invoice`,
            href: {
              pathname: "/invoices/for-invoicing/create-invoice",
            },
          },
          {
            title: () => t`New request for proforma invoice`,
            href: {
              pathname: "/invoices/for-invoicing/create-proforma",
            },
          },
          {
            title: () => t`New credit note`,
            href: {
              pathname: "/invoices/for-invoicing/create-credit-note",
            },
          },
          {
            title: () =>
              getEditTitleFromType(rfiType.data?.requestForInvoicing?.type) ??
              t`Edit`,
            href: {
              pathname: "/invoices/for-invoicing/edit",
              query: {
                id: invoiceId,
              },
            },
          },
        ],
      },
    ]);

  return {
    activePage,
    activeSubpage,
    allPages,
    visiblePages,
    data: data?.getSubmittedRfiCount,
    isLoading: loading,
  };
}
